// resources/js/Components/zone/ScheduleDrawer.jsx
import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    Divider,
    Switch,
    FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ManualScheduleForm from './ManualScheduleForm';
import SmartScheduler from './SmartScheduler';
import ScheduleSummary from './ScheduleSummary';

/**
 * Normalize schedule rows so all save handlers receive consistent HH:MM times.
 */
const normalizeSchedule = (schedule = []) => {
    return schedule.map((entry) => ({
        ...entry,
        start: String(entry?.start ?? '').slice(0, 5),
        stop: String(entry?.stop ?? '').slice(0, 5),
        days: Array.isArray(entry?.days) ? entry.days : [],
    }));
};

/**
 * Render the shared armed schedule drawer used by zone and group bulk edits.
 */
const ScheduleDrawer = ({
    open,
    onClose,
    initialSchedule,
    onSave,
    title = 'Edit Armed Schedule',
    headerContent = null,
    saveLabel = 'Save',
    emptyScheduleLabel,
    disabled = false,
}) => {
    const [mode, setMode] = useState('manual'); // 'manual' or 'grid'
    const [schedule, setSchedule] = useState(initialSchedule || []);

    useEffect(() => {
        setSchedule(JSON.parse(JSON.stringify(initialSchedule || [])));
        if (open) setMode('manual');
    }, [initialSchedule, open]);

    const handleSave = async () => {
        const shouldClose = await onSave(normalizeSchedule(schedule));

        if (shouldClose === false) {
            return;
        }

        setMode('manual');
        onClose();
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 'min(1000px, 100vw)', p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{title}</Typography>
                    <IconButton onClick={onClose} disabled={disabled}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {headerContent ? <Box mt={2}>{headerContent}</Box> : null}

                <Box mt={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={mode === 'grid'}
                                onChange={(e) => setMode(e.target.checked ? 'grid' : 'manual')}
                                disabled={disabled}
                            />
                        }
                        label="Use Interactive Grid"
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                {mode === 'manual' ? (
                    <ManualScheduleForm schedule={schedule} setSchedule={setSchedule} />
                ) : (
                    <SmartScheduler schedule={schedule} setSchedule={setSchedule} />
                )}

                <Divider sx={{ my: 2 }} />

                <ScheduleSummary schedule={schedule} showTimeline emptyLabel={emptyScheduleLabel} />

                <Box display="flex" justifyContent="space-between" mt={3}>
                    <Button onClick={onClose} disabled={disabled}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={disabled}>
                        {disabled ? <CircularProgress size={20} color="inherit" /> : saveLabel}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

export default ScheduleDrawer;
